import { useEffect, useState } from 'react'
import type { ComponentType } from 'react'
import { Alert, Spinner } from '@ztechapps/webui.util'

// The session token this whole area depends on lives in browser storage, unavailable during
// server rendering — so the authenticated bundle (SupportRoutes.tsx, its ApolloProvider, and
// every page under it) is never imported on the SSR pass, only dynamically on the client. Both
// the server render and the client's first hydration pass render the identical `null` fallback
// (SupportRoutes is undefined on both), so there is no hydration mismatch to recover from; the
// effect below is client-only and swaps in the real bundle once its chunk lands.
export const SupportArea = () => {
  const [Component, setComponent] = useState<ComponentType | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    // The bundle's own Bootstrap CSS loads alongside its JS, client-only — never in the SSR pass
    // that renders the marketing pages, which style themselves entirely off brand.css's bespoke
    // classes (several of which, e.g. `.btn`/`.btn-primary`, collide by name with Bootstrap's).
    Promise.all([import('./SupportRoutes'), import('./assets/bootstrap5.scss')])
      .then(([mod]) => {
        if (!cancelled) setComponent(() => mod.default)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  // `support-app` is the scoping root ./assets/bootstrap5.scss nests its whole Bootstrap import
  // under, so the QuickScore theme it carries — and every class it compiles — only ever paints
  // inside this subtree, never the marketing pages sharing this same document. Wraps all three
  // branches (failed, loading, loaded) so the loading/error fallbacks below can use Bootstrap
  // utility classes too.
  return (
    <div className="support-app">
      {failed && (
        <div className="container py-4">
          <Alert type="danger" text="Support could not be loaded. Please refresh the page." />
        </div>
      )}
      {!failed && !Component && (
        <div className="container py-5 text-center">
          <Spinner animation="border" />
        </div>
      )}
      {!failed && Component && <Component />}
    </div>
  )
}
